<script setup>
// NOOVI (cliente 2026-07-03): mostra o histórico paginado dos cards de Pipeline
// de um contato, em qualquer pipeline. Espelha ContactAppointmentHistory e
// reaproveita o endpoint contact-scoped (pipeline_cards?contact_id=X).
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import PipelinesAPI from 'dashboard/api/pipelines';
import { normalizeLocale } from 'dashboard/routes/dashboard/pipeline/utils/localeFormatting';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const { t, locale } = useI18n();
const route = useRoute();
const currentAccountId = computed(() => route.params.accountId);

const cards = ref([]);
const pipelines = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const error = ref(null);
const paginationError = ref(null);
const hasMore = ref(false);
const nextCursor = ref(null);
const filterStatus = ref('all');
const filterOptions = computed(() => [
  { value: 'all', label: t('PIPELINE.RELATED.FILTER_ALL') },
  { value: 'open', label: t('PIPELINE.CARD.STATUS.OPEN') },
  { value: 'won', label: t('PIPELINE.CARD.STATUS.WON') },
  { value: 'lost', label: t('PIPELINE.CARD.STATUS.LOST') },
]);
const PAGE_LIMIT = 50;
let loadRequestId = 0;
const loadedCursors = new Set();

const isCurrentContext = (requestId, accountId, contactId) =>
  requestId === loadRequestId &&
  String(accountId) === String(currentAccountId.value) &&
  String(contactId) === String(props.contactId);

const resetHistory = () => {
  loadRequestId += 1;
  cards.value = [];
  pipelines.value = [];
  loading.value = false;
  loadingMore.value = false;
  error.value = null;
  paginationError.value = null;
  hasMore.value = false;
  nextCursor.value = null;
  filterStatus.value = 'all';
  loadedCursors.clear();
};

const pipelineById = computed(() =>
  pipelines.value.reduce((map, p) => ({ ...map, [p.id]: p }), {})
);

const filteredCards = computed(() => {
  if (filterStatus.value === 'all') return cards.value;
  return cards.value.filter(c => {
    if (filterStatus.value === 'won') return !!c.won_at;
    if (filterStatus.value === 'lost') return !!c.lost_at;
    if (filterStatus.value === 'open') return !c.won_at && !c.lost_at;
    return true;
  });
});

const statusBadgeClass = card => {
  if (card.won_at) return 'bg-n-teal-3 text-n-teal-11';
  if (card.lost_at) return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-alpha-1 text-n-slate-11';
};

const statusLabel = card => {
  if (card.won_at) return t('PIPELINE.CARD.STATUS.WON');
  if (card.lost_at) return t('PIPELINE.CARD.STATUS.LOST');
  return t('PIPELINE.CARD.STATUS.OPEN');
};

const pipelineName = card =>
  pipelineById.value[card.pipeline_id]?.name || card.pipeline?.name || '—';

// Resolve stage names per card's OWN pipeline (cards span multiple pipelines,
// so there is no single "selected pipeline" to lean on here).
const stageName = card => {
  const pipeline = pipelineById.value[card.pipeline_id];
  return pipeline?.stages?.[card.pipeline_stage]?.name || card.pipeline_stage;
};

const formatRelativeTime = dateStr => {
  if (!dateStr) return '';
  try {
    const timestamp = new Date(dateStr).getTime();
    if (Number.isNaN(timestamp)) return '';
    const difference = timestamp - Date.now();
    const formatter = new Intl.RelativeTimeFormat(
      normalizeLocale(locale?.value),
      { numeric: 'auto' }
    );
    const minutes = Math.round(difference / 60000);
    if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
    const hours = Math.round(difference / 3600000);
    if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
    return formatter.format(Math.round(difference / 86400000), 'day');
  } catch {
    return '';
  }
};

const generatePipelineSlug = pipeline => {
  if (!pipeline) return '';
  const name = pipeline.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${pipeline.id}-${name}`;
};

const cardRoute = card => {
  const pipeline = pipelineById.value[card.pipeline_id];
  if (!pipeline) return null;
  return {
    name: 'pipeline_item_detail',
    params: {
      accountId: route.params.accountId,
      pipelineSlug: generatePipelineSlug(pipeline),
      view: 'item',
      itemId: card.id,
    },
  };
};

const pageData = response => {
  const data = response?.data;
  return {
    cards: data?.data ?? (Array.isArray(data) ? data : []),
    hasMore: Boolean(data?.meta?.has_more),
    nextCursor: data?.meta?.next_cursor || null,
  };
};

const applyPage = (response, requestedCursor = null) => {
  const page = pageData(response);
  if (
    page.hasMore &&
    (!page.nextCursor ||
      page.nextCursor === requestedCursor ||
      loadedCursors.has(page.nextCursor))
  ) {
    throw new Error('invalid pagination cursor');
  }

  const cardsById = new Map(
    cards.value.map((card, index) => [
      String(card?.id ?? `contact-card-${index}`),
      card,
    ])
  );
  page.cards.forEach((card, index) => {
    const key = card?.id ?? `contact-card-${cardsById.size + index}`;
    cardsById.set(String(key), card);
  });
  cards.value = [...cardsById.values()];
  hasMore.value = page.hasMore;
  nextCursor.value = page.hasMore ? page.nextCursor : null;
  if (requestedCursor) loadedCursors.add(requestedCursor);
};

const load = async () => {
  resetHistory();
  const requestId = loadRequestId;
  const contactId = props.contactId;
  const accountId = currentAccountId.value;
  if (!accountId || !contactId) {
    return;
  }

  loading.value = true;
  try {
    const [pipelinesRes, firstCardsRes] = await Promise.all([
      PipelinesAPI.get(accountId),
      PipelineCardsAPI.getCards(null, {
        contact_id: contactId,
        limit: PAGE_LIMIT,
      }),
    ]);
    if (!isCurrentContext(requestId, accountId, contactId)) return;

    pipelines.value = pipelinesRes.data || [];
    applyPage(firstCardsRes);
  } catch {
    if (!isCurrentContext(requestId, accountId, contactId)) return;
    error.value = t('PIPELINE.RELATED.LOAD_ERROR');
    cards.value = [];
  } finally {
    if (isCurrentContext(requestId, accountId, contactId)) {
      loading.value = false;
    }
  }
};

const loadMore = async () => {
  const cursor = nextCursor.value;
  if (!hasMore.value || !cursor || loading.value || loadingMore.value) return;

  const requestId = loadRequestId;
  const contactId = props.contactId;
  const accountId = currentAccountId.value;
  loadingMore.value = true;
  paginationError.value = null;
  try {
    const response = await PipelineCardsAPI.getCards(null, {
      contact_id: contactId,
      limit: PAGE_LIMIT,
      cursor,
    });
    if (!isCurrentContext(requestId, accountId, contactId)) return;
    applyPage(response, cursor);
  } catch {
    if (!isCurrentContext(requestId, accountId, contactId)) return;
    paginationError.value = t('PIPELINE.RELATED.LOAD_ERROR');
  } finally {
    if (isCurrentContext(requestId, accountId, contactId)) {
      loadingMore.value = false;
    }
  }
};

watch([currentAccountId, () => props.contactId], load, {
  immediate: true,
  flush: 'sync',
});
</script>

<template>
  <div class="px-6">
    <div class="flex items-center justify-between gap-2 flex-wrap mb-3">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('PIPELINE.CONTACT_TAB.TITLE') }}
        <span v-if="cards.length" class="text-sm font-normal text-n-slate-11">
          {{ t('PIPELINE.CONTACT_TAB.COUNT', { count: cards.length }) }}
        </span>
      </h3>
      <label for="contact-pipeline-history-filter" class="sr-only">
        {{ t('PIPELINE.RELATED.FILTER_LABEL') }}
      </label>
      <Select
        v-model="filterStatus"
        input-id="contact-pipeline-history-filter"
        :aria-label="t('PIPELINE.RELATED.FILTER_LABEL')"
        :options="filterOptions"
      />
    </div>

    <div
      v-if="error"
      class="flex flex-col items-start gap-3 py-4 text-xs text-n-ruby-11"
      role="alert"
    >
      <span>{{ error }}</span>
      <button
        type="button"
        class="min-h-11 rounded-lg border border-n-ruby-6 px-3 py-2 font-medium transition-colors hover:bg-n-ruby-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        @click="load"
      >
        {{ t('PIPELINE.RELATED.RETRY') }}
      </button>
    </div>

    <div
      v-else-if="loading"
      class="py-8 text-center"
      role="status"
      aria-live="polite"
      :aria-label="t('PIPELINE.LOADING')"
    >
      <span
        class="inline-block w-6 h-6 border-2 border-n-weak border-t-n-brand rounded-full motion-safe:animate-spin motion-reduce:animate-none"
        aria-hidden="true"
      />
    </div>

    <div v-else>
      <div
        v-if="!filteredCards.length"
        class="text-xs text-n-slate-11 italic py-4"
      >
        {{ t('PIPELINE.CONTACT_TAB.EMPTY') }}
      </div>

      <ul v-else class="space-y-2">
        <li
          v-for="card in filteredCards"
          :key="card.id"
          class="rounded-lg bg-n-solid-3"
        >
          <component
            :is="cardRoute(card) ? 'RouterLink' : 'div'"
            :to="cardRoute(card) || undefined"
            :aria-disabled="cardRoute(card) ? undefined : 'true'"
            class="flex min-h-11 w-full items-start justify-between gap-3 rounded-lg p-4 text-left transition-colors hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <h4 class="m-0 font-medium text-n-slate-12 truncate">
                  {{ card.title || t('PIPELINE.UNTITLED_ITEM') }}
                </h4>
                <span
                  class="flex-shrink-0 text-xs px-2 py-0.5 rounded-full"
                  :class="statusBadgeClass(card)"
                >
                  {{ statusLabel(card) }}
                </span>
              </div>
              <div
                class="flex items-center gap-1.5 text-xs text-n-slate-11 flex-wrap"
              >
                <span>{{ pipelineName(card) }}</span>
                <span
                  class="size-1 rounded-full bg-n-slate-8"
                  aria-hidden="true"
                />
                <span>{{ stageName(card) }}</span>
              </div>
              <div class="flex items-center gap-2 mt-2 text-xs text-n-slate-10">
                <Avatar
                  :name="card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')"
                  :src="card.owner?.avatar_url"
                  :size="20"
                />
                <span>{{
                  card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')
                }}</span>
                <span
                  class="size-1 rounded-full bg-n-slate-8"
                  aria-hidden="true"
                />
                <span>{{ formatRelativeTime(card.updated_at) }}</span>
              </div>
            </div>
            <span
              class="flex min-h-11 flex-shrink-0 items-center rounded bg-n-brand px-3 py-2 text-xs font-medium text-white"
            >
              {{ t('PIPELINE.RELATED.VIEW_ITEM') }}
            </span>
          </component>
        </li>
      </ul>

      <div
        v-if="hasMore || paginationError"
        class="flex flex-col items-center gap-2 py-4"
      >
        <span
          v-if="paginationError"
          class="text-xs text-n-ruby-11"
          role="alert"
        >
          {{ paginationError }}
        </span>
        <button
          type="button"
          class="min-h-11 rounded-lg border border-n-strong px-4 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="contact-pipeline-load-more"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{
            loadingMore ? t('PIPELINE.LOADING') : t('PIPELINE.LOAD_MORE_SIMPLE')
          }}
        </button>
      </div>
    </div>
  </div>
</template>
